// The big map (polish pass 2026-10-15; HOLLOWMARK 21.5, Diablo's Tab map, Zomboid's paper map). N, or a tap on the
// minimap, opens the whole block drawn in the game's own iso projection (so up is the same up), with the venues named,
// you and your friends marked, and everywhere you have not been yet under fog. What you have seen is remembered on this
// device (localStorage, a per-viewer convenience: losing it only brings the fog back).
import type { IsoMap } from '../../shared/src/iso';
import { CELL_PX } from '../../shared/src/iso';
import { keyName } from './options';

const B = 8;                                   // fog blocks of 8 x 8 cells (4 m)

const PINNED = new Set(['tavern', 'blackdog', 'beercade']);   // the friends' bars on the block

export class BigMap {
  el: HTMLDivElement; cv: HTMLCanvasElement; g: CanvasRenderingContext2D;
  base: HTMLCanvasElement; fog: HTMLCanvasElement; seen: Uint8Array; bw: number; bh: number;
  open = false; dirty = true; saveT = 0;
  /** street names, found from the ground layer: the long east-west asphalt band is Whyte Ave, the north-south runs
   *  crossing it are 106, 105 and 104 St from west to east (R11-007; the block is 104-106 St) */
  streets: { name: string; x: number; y: number }[] = [];

  /** landmarks (world.json): pinned from the first look, like the street names, so you can give directions from them (R11-007) */
  constructor(public map: IsoMap, base: HTMLCanvasElement, public venues: Record<string, { name?: string; door?: [number, number] }>, public landmarks: { t: string; x0: number; y0: number; x1: number; y1: number }[] = []) {
    this.base = base;
    this.bw = Math.ceil(map.w / B); this.bh = Math.ceil(map.h / B);
    this.seen = new Uint8Array(this.bw * this.bh);
    try { const s = localStorage.getItem('dm_seen_street'); if (s) { const b = atob(s); if (b.length === this.seen.length) for (let i = 0; i < b.length; i++) this.seen[i] = b.charCodeAt(i); } } catch { /* private mode */ }
    this.fog = document.createElement('canvas'); this.fog.width = this.bw; this.fog.height = this.bh;
    this.el = document.createElement('div'); this.el.id = 'bigmap'; this.el.hidden = true;
    this.el.innerHTML = '<canvas></canvas><div class="bm-hd">WHYTE AVE <small>[N] or tap to close</small></div>';
    document.body.appendChild(this.el);
    this.cv = this.el.querySelector('canvas')!; this.g = this.cv.getContext('2d')!;
    this.el.addEventListener('pointerdown', (e) => { e.preventDefault(); this.toggle(false); });
    const road = (i: number) => { const g = map.g[i]; return g === 1 || g === 2 || g === 3 || g === 15; };
    let bestY = 0, bestN = 0;
    for (let y = 0; y < map.h; y++) { let n = 0; for (let x = 0; x < map.w; x++) if (road(x + y * map.w)) n++; if (n > bestN) { bestN = n; bestY = y; } }
    this.streets.push({ name: 'WHYTE AVE (82 AVE)', x: map.w * 0.3, y: bestY + 4 });
    const colRoad: number[] = []; for (let x = 0; x < map.w; x++) { let n = 0; for (let y = 0; y < map.h; y++) if (Math.abs(y - bestY) > 30 && road(x + y * map.w)) n++; colRoad.push(n); }
    const runs: number[] = []; let st = -1;
    for (let x = 0; x <= map.w; x++) { const on = x < map.w && colRoad[x] > map.h * 0.25; if (on && st < 0) st = x; if (!on && st >= 0) { if (x - st >= 6) runs.push((st + x) / 2); st = -1; } }
    const names = ['106 ST', '105 ST', '104 ST'];
    runs.slice(0, 3).forEach((x, k) => this.streets.push({ name: names[k + Math.max(0, 3 - runs.length) * 0] ?? '', x, y: bestY - 40 }));
  }

  /** you are at world px (x, y): clear the fog for about 20 m around you */
  mark(x: number, y: number) {
    const cx = Math.floor(x / CELL_PX / B), cy = Math.floor(y / CELL_PX / B), R = 5;
    for (let dy = -R; dy <= R; dy++) for (let dx = -R; dx <= R; dx++) {
      if (dx * dx + dy * dy > R * R) continue;
      const X = cx + dx, Y = cy + dy; if (X < 0 || Y < 0 || X >= this.bw || Y >= this.bh) continue;
      const i = X + Y * this.bw; if (!this.seen[i]) { this.seen[i] = 1; this.dirty = true; }
    }
    if (this.dirty && performance.now() > this.saveT) {
      this.saveT = performance.now() + 5000;
      try { let s = ''; for (const v of this.seen) s += String.fromCharCode(v); localStorage.setItem('dm_seen_street', btoa(s)); } catch { /* */ }
    }
  }

  toggle(on = !this.open) {
    this.open = on; this.el.hidden = !on;
    // R5-016: the header names the key you bound for the map
    const hd = this.el.querySelector('.bm-hd small'); if (on && hd) hd.textContent = `[${keyName('map').split(' / ')[0]}] or tap to close`;
  }

  draw(me: { x: number; y: number }, others: { x: number; y: number; c: string; label?: string; goal?: boolean }[]) {
    if (!this.open) return;
    const W = this.cv.width = Math.round(innerWidth * 0.92), H = this.cv.height = Math.round(innerHeight * 0.82);
    const g = this.g, m = this.map;
    // fit the iso diamond of the map: screen x spans (x - y), screen y spans (x + y) / 2
    const spanX = m.w + m.h, spanY = (m.w + m.h) / 2;
    let k = Math.min(W / spanX, H / spanY) * 0.98, ox = W / 2 - (m.w - m.h) / 2 * k, oy = H / 2 - (m.w + m.h) / 4 * k;
    // studio 009f: framed on you and where your step sends you (60 m round them, 200 m across at least), never further out
    // than the whole block. Whole, the 1.5 km of Whyte was a thin diagonal across a portrait phone with its names piled on
    // each other, and the 2026-10-28 phone playtester could not find the Pint on it
    { const pts = [me, ...others.filter(o => o.goal)].map(p => { const cx = p.x / CELL_PX, cy = p.y / CELL_PX; return [cx - cy, (cx + cy) / 2]; });
      const us = pts.map(p => p[0]), vs = pts.map(p => p[1]), pad = 120;
      let u0 = Math.min(...us) - pad, u1 = Math.max(...us) + pad, v0 = Math.min(...vs) - pad / 2, v1 = Math.max(...vs) + pad / 2;
      const su = Math.max(u1 - u0, 400, (v1 - v0) * W / H), sv = Math.max(v1 - v0, su * H / W), uc = (u0 + u1) / 2, vc = (v0 + v1) / 2;
      const kf = Math.min(W / su, H / sv);
      if (kf > k) { k = kf; ox = W / 2 - uc * k; oy = H / 2 - vc * k; } }
    g.fillStyle = '#0f0e22'; g.fillRect(0, 0, W, H);
    g.imageSmoothingEnabled = false;
    g.setTransform(k, k / 2, -k, k / 2, ox, oy);
    g.drawImage(this.base, 0, 0);
    if (this.dirty) {
      const fg = this.fog.getContext('2d')!; const img = fg.createImageData(this.bw, this.bh);
      for (let i = 0; i < this.seen.length; i++) { img.data[i * 4 + 3] = this.seen[i] ? 0 : 90; /* R10-006: 225 hid the block; 140 left the L* spread at 0.206, 118 at 0.241 (H9.3 wants 0.25) */ img.data[i * 4] = 15; img.data[i * 4 + 1] = 14; img.data[i * 4 + 2] = 34; }
      fg.putImageData(img, 0, 0); this.dirty = false;
    }
    g.imageSmoothingEnabled = true;
    g.drawImage(this.fog, 0, 0, this.bw * B, this.bh * B);
    g.setTransform(1, 0, 0, 1, 0, 0);
    const P = (x: number, y: number): [number, number] => { const cx = x / CELL_PX, cy = y / CELL_PX; return [ox + (cx - cy) * k, oy + (cx + cy) * k / 2]; };
    g.font = `bold ${Math.max(10, Math.round(k * 9))}px monospace`; g.textAlign = 'center';
    for (const v of Object.values(this.venues)) {
      if (!v?.door || !v.name) continue;
      const bi = Math.floor(v.door[0] / B) + Math.floor(v.door[1] / B) * this.bw;
      if (!this.seen[bi]) continue;                       // a place is named once you have been near it
      const [sx, sy] = P(v.door[0] * CELL_PX, v.door[1] * CELL_PX);
      g.fillStyle = '#000'; g.fillText(v.name, sx + 1, sy - 5); g.fillStyle = '#ffd38a'; g.fillText(v.name, sx, sy - 6);
      g.fillRect(sx - 1.5, sy - 1.5, 3, 3);
    }
    g.font = `bold ${Math.max(10, Math.round(k * 10))}px monospace`;
    for (const st of this.streets) { const [sx, sy] = P(st.x * CELL_PX, st.y * CELL_PX); g.fillStyle = '#000'; g.fillText(st.name, sx + 1, sy + 1); g.fillStyle = '#cfc6b0'; g.fillText(st.name, sx, sy); }
    // the friends' bars and the Princess are pinned from the first look too (R8-011), like the clock tower
    for (const [k, v] of Object.entries(this.venues)) {
      if (!v?.door || !v.name || !PINNED.has(k)) continue;
      const bi = Math.floor(v.door[0] / B) + Math.floor(v.door[1] / B) * this.bw;
      if (this.seen[bi]) continue;                        // already named by the seen pass above
      const [sx, sy] = P(v.door[0] * CELL_PX, v.door[1] * CELL_PX);
      g.fillStyle = '#000'; g.fillText(v.name, sx + 1, sy - 5); g.fillStyle = '#ffd38a'; g.fillText(v.name, sx, sy - 6);
      g.fillRect(sx - 1.5, sy - 1.5, 3, 3);
    }
    for (const L of this.landmarks as any[]) {
      if (L.t !== 'label') continue;
      const [sx, sy] = P((L.x0 + L.x1) / 2 * CELL_PX, (L.y0 + L.y1) / 2 * CELL_PX);
      g.fillStyle = '#000'; g.fillText(L.name, sx + 1, sy - 7); g.fillStyle = '#e8d8b0'; g.fillText(L.name, sx, sy - 8);
      g.fillRect(sx - 2, sy - 2, 4, 4);
    }
    for (const L of this.landmarks) {
      if (L.t !== 'clocktower') continue;
      const [sx, sy] = P((L.x0 + L.x1) / 2 * CELL_PX, (L.y0 + L.y1) / 2 * CELL_PX);
      g.fillStyle = '#000'; g.fillText('CLOCK TOWER', sx + 1, sy - 7); g.fillStyle = '#e8d8b0'; g.fillText('CLOCK TOWER', sx, sy - 8);
      g.fillRect(sx - 2, sy - 2, 4, 4);
    }
    for (const o of others) { const [sx, sy] = P(o.x, o.y); g.fillStyle = o.c; g.fillRect(sx - 2, sy - 2, 4, 4);
      if (o.label) { g.fillRect(sx - 3, sy - 3, 6, 6); g.fillStyle = '#000'; g.fillText(o.label, sx + 1, sy - 7); g.fillStyle = o.c; g.fillText(o.label, sx, sy - 8); } }   // a party member, named (R7-015)
    const [mx, my] = P(me.x, me.y);
    g.strokeStyle = '#0f0e22'; g.lineWidth = 3; g.beginPath(); g.arc(mx, my, 5, 0, Math.PI * 2); g.stroke();
    g.fillStyle = '#8fd0ff'; g.beginPath(); g.arc(mx, my, 4, 0, Math.PI * 2); g.fill();   // you: the one light-blue mark (STYLE H3, R10-018)
  }
}
